import type {
  AssistantMessage,
  ToolMessage,
  UIMessage,
  UIMessagePart,
  CoreMessage,
} from "@/lib/types/ai-adapter";
import { type ClassValue, clsx } from "clsx";
import { formatISO } from "date-fns";
import { twMerge } from "tailwind-merge";
import { ChatSDKError, type ErrorCode } from "./errors";
import logger from "@/lib/logger";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function debugLog(message: string, data?: any) {
  logger.debug({ data }, message);
}

export const fetcher = async (url: string) => {
  const response = await fetch(url);

  if (!response.ok) {
    const { code, cause } = await response.json();
    throw new ChatSDKError(code as ErrorCode, cause);
  }

  return response.json();
};

export async function fetchWithErrorHandlers(
  input: RequestInfo | URL,
  init?: RequestInit
) {
  try {
    const response = await fetch(input, init);

    if (!response.ok) {
      const { code, cause } = await response.json();
      throw new ChatSDKError(code as ErrorCode, cause);
    }

    return response;
  } catch (error: unknown) {
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      throw new ChatSDKError("offline:chat");
    }

    throw error;
  }
}

export function getLocalStorage(key: string) {
  if (typeof window !== "undefined") {
    return JSON.parse(localStorage.getItem(key) || "[]");
  }
  return [];
}

export function generateUUID(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

type ResponseMessageWithoutId = ToolMessage | AssistantMessage;
type ResponseMessage = ResponseMessageWithoutId & { id: string };

export function getMostRecentUserMessage(messages: UIMessage[]) {
  const userMessages = messages.filter((message) => message.role === "user");
  return userMessages.at(-1);
}

// Removed getDocumentTimestampByIndex (Document table removed)

export function getTrailingMessageId({
  messages,
}: {
  messages: ResponseMessage[];
}): string | null {
  const trailingMessage = messages.at(-1);

  if (!trailingMessage) {
    return null;
  }

  return trailingMessage.id;
}

export function sanitizeText(text: string) {
  return text.replace("<has_function_call>", "");
}

/**
 * Escapes HTML special characters to prevent XSS.
 */
export function escapeHtml(unsafe: string): string {
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

// Removed convertToUIMessages (DBMessage type removed)

export function filterToolMessages(messages: UIMessage[]): UIMessage[] {
  // Use reduce to process messages, allowing us to merge consecutive assistant turns.
  return messages.reduce((acc, current) => {
    if (!current.parts || current.parts.length === 0) {
      return acc;
    }

    const lastMessage = acc.length > 0 ? acc[acc.length - 1] : undefined;

    // Squash consecutive user messages
    if (lastMessage && lastMessage.role === "user" && current.role === "user") {
      debugLog("Merging consecutive user messages", {
        lastId: lastMessage.id,
        currentId: current.id,
      });
      // Assuming user messages are primarily text. We'll merge the text parts.
      // If structured parts exist, a more complex merge strategy might be needed.
      const currentTextParts = current.parts.filter((p) => p.type === "text");
      if (currentTextParts.length > 0) {
        const newParts = [...(lastMessage.parts || []), ...currentTextParts];
        // Update the last message with merged parts
        acc[acc.length - 1] = {
          ...lastMessage,
          parts: newParts,
        };
        return acc;
      }
    }

    // Check for a potential merge: an assistant message followed by another assistant message.
    if (
      lastMessage &&
      lastMessage.role === "assistant" &&
      current.role === "assistant"
    ) {
      // Check if the previous assistant message is text-only.
      const isLastTextOnly =
        lastMessage.parts &&
        lastMessage.parts.length > 0 &&
        lastMessage.parts.every((part) => part.type === "text");

      // Check if the current assistant message has a tool call.
      const isCurrentToolCall =
        current.parts &&
        current.parts.some((part) => part.type === "tool-call");

      // If the conditions are met, merge the last message into the current one.
      if (isLastTextOnly && isCurrentToolCall) {
        debugLog("Merging assistant text message into assistant tool call", {
          lastId: lastMessage.id,
          currentId: current.id,
        });
        // Combine the parts from both messages.
        const mergedMessage: UIMessage = {
          ...current,
          parts: [...(lastMessage.parts || []), ...(current.parts || [])],
        };

        // Replace the last message in the accumulator with the new merged message.
        acc[acc.length - 1] = mergedMessage;
        return acc;
      }
    }

    // Check for "hanging" tool calls (Tool -> User transition without an Assistant response)
    // This ensures the turn-taking sequence (Tool -> Assistant -> User) is maintained.
    if (
      lastMessage &&
      (lastMessage.role as string) === "tool" &&
      current.role === "user"
    ) {
      debugLog(
        "Auto-healing turn-taking: Inserting synthetic assistant message after tool call",
        { lastId: lastMessage.id }
      );
      acc.push({
        id: generateUUID(),
        role: "assistant",
        content: "Action completed.", // Ensure content is present
        parts: [{ type: "text", text: "Action completed." }],
        createdAt: new Date(),
      } as UIMessage);
    }

    // If no merge is needed, just add the current message to the accumulator.
    acc.push(current);
    return acc;
  }, [] as UIMessage[]);
}

export function reduceMessageWindow(
  uiMessages: UIMessage[],
  contextWindowSize: number | string | undefined
): UIMessage[] {
  const size =
    typeof contextWindowSize === "string"
      ? parseInt(contextWindowSize, 10)
      : contextWindowSize;

  if (size && size > 0) {
    return uiMessages.slice(-size);
  }
  return uiMessages;
}

export function getTextFromMessage(message: UIMessage): string {
  if (!message.parts) return "";
  return message.parts
    .filter((part) => part.type === "text")
    .map((part) => (part as { type: "text"; text: string }).text)
    .join("");
}

export function normalizeMessages(messages: UIMessage[]): UIMessage[] {
  const normalized: UIMessage[] = [];

  for (const msg of messages) {
    const lastMsg = normalized[normalized.length - 1];
    // If current and last message are both 'assistant', MERGE them
    if (lastMsg && lastMsg.role === "assistant" && msg.role === "assistant") {
      // Merge parts: append current message's parts to the last message
      if (msg.parts) {
        if (!lastMsg.parts) lastMsg.parts = [];
        lastMsg.parts.push(...msg.parts);
      }
    } else {
      normalized.push(msg);
    }
  }

  return normalized;
}

// NOTE: This function logic might need adjustment based on how CoreMessage is used now.
// Previously it handled string or array content.
// CoreMessage.content can still be string or array.
export function normalizeCoreMessages(messages: CoreMessage[]): CoreMessage[] {
  const normalized: CoreMessage[] = [];
  // NOT IMPLEMENTED: Detailed merging logic for CoreMessage variants.
  // Assuming simpler usage for now or that it's not critical.
  // If we need to merge content, we have to check types carefully (AssistantContent, ToolContent etc).

  // For now, just pass through.
  return messages;
}

export function getChatPath({
  id,
  campaignId,
}: {
  id: string;
  campaignId?: string | null;
}) {
  return campaignId ? `/campaigns/${campaignId}` : `/chat/${id}`;
}

/**
 * Returns the resolved and normalized URL for the Kernel service.
 * Prioritizes REMOTE_KERNEL_URL for external service-to-service communication.
 */
export function getKernelUrl(path = ""): URL {
  const baseUrl =
    process.env.REMOTE_KERNEL_URL ||
    process.env.KERNEL_URL ||
    "http://localhost:8080";

  if (!path) {
    return new URL(baseUrl);
  }

  // To correctly join a path to a base URL that might have its own path,
  // we ensure the base URL is treated like a directory (ends with '/')
  // and the path to join is relative (does not start with '/').
  const baseWithSlash = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  const relativePath = path.startsWith("/") ? path.substring(1) : path;

  return new URL(relativePath, baseWithSlash);
}
